import type { BillingPayer, BillingPlan, BillingStatus } from '../../types'

const statusStyles: Record<BillingStatus, string> = {
  active: 'bg-green-50 text-green-700',
  trialing: 'bg-charcoal/10 text-text-primary',
  past_due: 'bg-red-50 text-red-700',
  declined: 'bg-red-50 text-red-700',
  canceled: 'bg-stone/20 text-text-muted',
  paused: 'bg-stone/20 text-text-muted',
  incomplete: 'bg-amber-50 text-amber-700',
  no_payment: 'bg-amber-50 text-amber-700',
}

const statusLabels: Record<BillingStatus, string> = {
  active: 'Active',
  trialing: 'Trialing',
  past_due: 'Past due',
  declined: 'Payment declined',
  canceled: 'Canceled',
  paused: 'Paused',
  incomplete: 'Setup required',
  no_payment: 'No payment info',
}

export function BillingStatusBadge({ status, detail }: { status: BillingStatus; detail?: string | null }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide whitespace-nowrap ${statusStyles[status] || statusStyles.incomplete}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {statusLabels[status] || status}{detail ? ` · ${detail}` : ''}
    </span>
  )
}

export function PayerBadge({ payer }: { payer: BillingPayer }) {
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-bold uppercase tracking-wide whitespace-nowrap ${
      payer === 'guide' ? 'bg-[#F3EEE9] border-[#DCD2C8] text-[#351E1D]' : 'bg-charcoal/10 border-charcoal/20 text-text-primary'
    }`}>
      {payer === 'guide' ? 'Guide-paid' : 'Company-paid'}
    </span>
  )
}

export function PlanCard({ plan, selected, current, onClick, hidePrice = false }: {
  plan: BillingPlan
  selected?: boolean
  current?: boolean
  onClick?: () => void
  hidePrice?: boolean
}) {
  const active = selected || current
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`relative rounded-xl bg-white p-4 text-left border flex flex-col disabled:cursor-default ${
        active ? 'border-2 border-pinnacle-red shadow-card' : 'border-stone/20 hover:border-stone/40'
      }`}
    >
      {active && (
        <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-white bg-pinnacle-red">
          {current ? 'Current plan' : 'Selected'}
        </span>
      )}
      <span className="inline-flex self-start px-2 py-0.5 rounded-full text-xs font-semibold mb-2 bg-stone/15 text-text-muted">
        {plan.employee_band || plan.band}
      </span>
      <span className="text-sm font-bold text-text-primary">{plan.name}</span>
      {!hidePrice && plan.price_monthly !== null && <span className="mt-1 text-2xl font-extrabold text-text-primary">{plan.price_display || `$${plan.price_monthly}/mo`}</span>}
    </button>
  )
}

export function BillingEmpty({ children }: { children: React.ReactNode }) {
  return <div className="bg-white rounded-xl border border-stone/20 shadow-card px-6 py-12 text-center text-text-muted">{children}</div>
}